import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import {
    getErrorMessage,
    useMutationWithHandler,
} from '@/hooks/useMutationWithHandler'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { useState } from 'react'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { Button, Dialog } from 'vnetwork-ui'
import { SIGNED_DOCUMENTS } from '../../constants/signed-documents'
import { useSignedDocumentErrorText } from '../../hooks/use-signed-document-error-text'
import { getSignedDocumentPreview, resignSignedDocument } from '../../services'
import {
    SignedDocument,
    SignedDocumentImageSource,
    SignedDocumentType,
} from '../../types'
import ImageSourcePicker from './ImageSourcePicker'

interface ResignDialogProps {
    open: boolean
    onClose: () => void
    documentType: SignedDocumentType
    invoiceID: number
    /** Kết quả bản mới (signed / pending / failed), xử lý giống sau khi gửi ký lẻ */
    onResigned: (result: SignedDocument) => void
    /** Nguồn trang "Hình ảnh" cho bản mới (chỉ loại hasImages; dùng chung lựa chọn với popup chính) */
    imageSource: SignedDocumentImageSource
    onImageSourceChange: (value: SignedDocumentImageSource) => void
    /** Lỗi API: bản cũ có thể đã bị huỷ, parent đọc lại trạng thái */
    onFailed: () => void
}

/**
 * Ký lại chứng từ đã ký: huỷ bản cũ + ký bản mới theo dữ liệu hoá đơn hiện tại
 * (POST .../<path>/resign). Bắt buộc nhập lý do.
 */
const ResignDialog = ({
    open,
    onClose,
    documentType,
    invoiceID,
    onResigned,
    onFailed,
    imageSource,
    onImageSourceChange,
}: ResignDialogProps) => {
    const { t } = useTranslation('invoice')
    const config = SIGNED_DOCUMENTS[documentType]
    const documentNames = {
        document: t(`${config.i18nKey}.name`),
        Document: t(`${config.i18nKey}.name-capitalized`),
    }
    const errorText = useSignedDocumentErrorText(documentType)
    // Loại không có trang Hình ảnh: không gửi image_source
    const activeImageSource = config.hasImages ? imageSource : undefined
    const [reason, setReason] = useState('')
    const [showPreview, setShowPreview] = useState(false)

    const preview = useQueryWithHandler(
        {
            queryKey: [
                QUERY_KEYS.INVOICE_SIGNED_DOCUMENT_PREVIEW,
                documentType,
                invoiceID,
                activeImageSource ?? 'none',
            ],
            queryFn: () =>
                getSignedDocumentPreview(
                    documentType,
                    invoiceID,
                    activeImageSource
                ),
            enabled: showPreview,
            // Bản mới dựng từ dữ liệu hoá đơn hiện tại: không dùng cache 5 phút mặc định
            staleTime: 0,
        },
        {
            renderError: (error) => (
                <div className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-800">
                    {errorText(getErrorMessage(error), {
                        fallback: 'preview-failed',
                    })}
                </div>
            ),
        }
    )

    const { mutateAsync: resign, isPending } = useMutationWithHandler(
        { mutationFn: resignSignedDocument },
        {
            reactQueryKeys: QUERY_KEYS.INVOICES,
            onSuccess: (response) => {
                if (response?.result) onResigned(response.result)
                onClose()
            },
            // useMutationWithHandler không tra namespace invoice: tự toast bằng errorText
            showErrorToast: false,
            onError: (error) => {
                toast.error(errorText(getErrorMessage(error)))
                onFailed()
            },
        }
    )

    const trimmedReason = reason.trim()

    return (
        <Dialog
            open={open}
            onClose={() => !isPending && onClose()}
            maxWidth={showPreview ? 'lg' : 'md'}
            title={t('signed-document-sign-again-title', documentNames)}
            actions={
                <div className="flex w-full flex-wrap items-center justify-end gap-2">
                    {isPending && (
                        <span className="mr-auto text-sm text-gray-500">
                            {t('signed-document-signing-hint')}
                        </span>
                    )}
                    <Button
                        variant="outlined"
                        onClick={onClose}
                        disabled={isPending}
                    >
                        {t('close')}
                    </Button>
                    <Button
                        color="warning"
                        onClick={() =>
                            resign({
                                type: documentType,
                                invoiceID,
                                reason: trimmedReason,
                                imageSource: activeImageSource,
                            })
                        }
                        loading={isPending}
                        disabled={!trimmedReason || isPending}
                    >
                        {t('signed-document-sign-again-confirm')}
                    </Button>
                </div>
            }
        >
            <div className="space-y-3 text-sm">
                <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-2 text-amber-800">
                    {t('signed-document-sign-again-warning', documentNames)}
                </div>
                {config.hasImages && (
                    <ImageSourcePicker
                        type={documentType}
                        invoiceID={invoiceID}
                        value={imageSource}
                        onChange={onImageSourceChange}
                        disabled={isPending}
                    />
                )}
                <label className="block">
                    <span className="mb-1 block font-medium text-gray-700">
                        {t('signed-document-sign-again-reason')}{' '}
                        <span className="text-red-500">*</span>
                    </span>
                    <textarea
                        value={reason}
                        onChange={(event) => setReason(event.target.value)}
                        placeholder={t(
                            'signed-document-sign-again-reason-placeholder'
                        )}
                        rows={3}
                        disabled={isPending}
                        className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary focus:outline-none"
                    />
                </label>
                <button
                    type="button"
                    className="text-primary hover:underline"
                    onClick={() => setShowPreview((value) => !value)}
                >
                    {t(
                        showPreview
                            ? 'signed-document-sign-again-hide-preview'
                            : 'signed-document-sign-again-preview'
                    )}
                </button>
                {showPreview &&
                    (preview.renderState() ?? (
                        <div className="overflow-x-auto">
                            {/* Bản mới dựng từ dữ liệu hoá đơn hiện tại */}
                            <iframe
                                title={t(`${config.i18nKey}.title`)}
                                srcDoc={preview.data}
                                sandbox=""
                                className="h-[60vh] w-full min-w-[820px] border-0 bg-white"
                            />
                        </div>
                    ))}
            </div>
        </Dialog>
    )
}

export default ResignDialog
